import { FormControl, FormLabel, Stack } from '@mui/joy';
import { camelCase, upperFirst } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormField } from 'core/components';
import { FormGroupWrapper } from 'core/elements';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { QuoteEntityType, QuoteNoteType, QuoteType } from 'quotes/enums';
import { IBaseQuote } from 'quotes/types';
import { Size } from 'theme/enums';

import { QuoteNoteTypeToCopyFieldMap } from './constants';
import { CopyNoteWarning } from './CopyNoteWarning';
import { CopyNoteType, ItemsToCopyField } from './enums';

interface IProps {
  quote: IBaseQuote;
  targetQuote?: IBaseQuote;
  addToQuoteType: QuoteType;
  itemsToCopyValues: ItemsToCopyField[];
  entityType?: QuoteEntityType;
}

export const ItemsToCopyFormFields = ({
  quote,
  targetQuote,
  addToQuoteType = QuoteType.CUSTOMER,
  itemsToCopyValues,
  entityType = QuoteEntityType.QUOTE,
}: IProps) => {
  const [warnings, setWarnings] = useState<{
    [key in QuoteNoteType]?: boolean;
  }>({});

  const { t } = useTranslation();

  const { control, errors, watch, setValue } = useFormContext();

  const onChangeNoteField = (checked: boolean, noteType: QuoteNoteType) => {
    if (!checked) {
      return;
    }

    setWarnings((prev) => ({
      ...prev,
      [noteType]: true,
    }));
  };

  const onConfirmNoteWarning = (noteType: QuoteNoteType, copyNoteType: CopyNoteType) => {
    setWarnings((prev) => ({
      ...prev,
      [noteType]: false,
    }));

    const itemToCopyField = QuoteNoteTypeToCopyFieldMap[noteType];

    setValue(`${itemToCopyField}Type`, copyNoteType);
  };

  const onCloseNoteWarning = (noteType: QuoteNoteType) => {
    setWarnings((prev) => ({
      ...prev,
      [noteType]: false,
    }));

    setValue(QuoteNoteTypeToCopyFieldMap[noteType], false);
  };

  return (
    <>
      {!quote.isOrder &&
        QuoteType.CUSTOMER === addToQuoteType &&
        QuoteType.VENDOR === quote.type && (
          <FormField
            name="attachQuote"
            type={InputType.CHECKBOX}
            inputProps={{
              control,
              error: errors?.attachQuote,
              label: t('labelAttachAsVendorQuote'),
            }}
          />
        )}

      <Stack>
        <FormControl size={Size.MD}>
          <FormLabel>{t('labelCopy')}</FormLabel>
        </FormControl>

        <FormGroupWrapper
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          {itemsToCopyValues.map((field) => {
            let onChange = null;

            if (
              [ItemsToCopyField.QUOTE_CUSTOMER_NOTE, ItemsToCopyField.QUOTE_INTERNAL_NOTE].includes(
                field
              )
            ) {
              onChange = ({ target: { checked } }) =>
                onChangeNoteField(
                  checked,
                  Object.keys(QuoteNoteTypeToCopyFieldMap).find(
                    (key) => QuoteNoteTypeToCopyFieldMap[key as QuoteNoteType] === field
                  ) as QuoteNoteType
                );
            }

            const getLabel = () => {
              if (field === ItemsToCopyField.QUOTE_CUSTOMER_NOTE) {
                return quote?.type === QuoteType.VENDOR
                  ? t('labelQuoteVendorNote')
                  : t('labelQuoteCustomerNote');
              }

              return t(`label${upperFirst(camelCase(field))}`, { entityType });
            };

            return (
              <FormField
                key={field}
                name={field}
                type={InputType.CHECKBOX}
                inputProps={{
                  control,
                  error: errors?.[field],
                  label: getLabel(),
                  size: Size.SM,
                  disabled: ItemsToCopyField.PARTS === field,
                  onChange,
                }}
                fullWidth={false}
              />
            );
          })}
        </FormGroupWrapper>
      </Stack>

      {targetQuote?.id && (
        <>
          <CopyNoteWarning
            itemsToCopyValues={itemsToCopyValues}
            sourceQuote={quote}
            targetQuote={targetQuote}
            noteType={QuoteNoteType.CUSTOMER_NOTE}
            copyNoteValue={watch(ItemsToCopyField.QUOTE_CUSTOMER_NOTE)}
            isOpen={warnings[QuoteNoteType.CUSTOMER_NOTE]}
            onConfirm={onConfirmNoteWarning}
            onClose={onCloseNoteWarning}
          />

          <CopyNoteWarning
            itemsToCopyValues={itemsToCopyValues}
            sourceQuote={quote}
            targetQuote={targetQuote}
            noteType={QuoteNoteType.INTERNAL_NOTE}
            copyNoteValue={watch(ItemsToCopyField.QUOTE_INTERNAL_NOTE)}
            isOpen={warnings[QuoteNoteType.INTERNAL_NOTE]}
            onConfirm={onConfirmNoteWarning}
            onClose={onCloseNoteWarning}
          />
        </>
      )}
    </>
  );
};
